import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload, FaSave, FaTrash, FaEraser, FaTimes, FaSearch, FaSpinner } from 'react-icons/fa';
import PageHeader from '../../molecules/PageHeader';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import { createBatchProcessing, CreateBatchRequest, getColumnTemplates, saveColumnTemplate, deleteColumnTemplate } from '../../../services/CreditAnalysisService';
import appConfig from '../../../config/app-config';
const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

interface LegalPersonResult {
    personId: string;
    legalName: string;
    fantasyName: string;
    govTaxId: string;
    govTaxIdFormatted: string;
}

const TAGS = {
    PRODUCER_DOCUMENT: { name: 'CPF/CNPJ do Sacado', color: 'bg-blue-500' },
    DRAWEE: { name: 'Nome do Sacado', color: 'bg-green-500' },
    AMOUNT: { name: 'Valor Pretendido', color: 'bg-orange-500' },
} as const satisfies Record<string, { name: string; color: string }>;

type TagKey = keyof typeof TAGS;

const _STORAGE_KEY = 'credit-analysis-templates';

interface AssignorData {
    id: string;
    name: string;
    data: string[][];
    columnTags: Record<number, TagKey>;
    validationErrors: Map<number, Set<number>>;
}

const validateCPF = (cpf: string): boolean => {
    const cleaned = cpf.replaceAll(/\D/g, '');
    if (cleaned.length !== 11) return false;
    if (/^(\d)\1+$/.test(cleaned)) return false;
    
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += Number.parseInt(cleaned.charAt(i)) * (10 - i);
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== Number.parseInt(cleaned.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) sum += Number.parseInt(cleaned.charAt(i)) * (11 - i);
    digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== Number.parseInt(cleaned.charAt(10))) return false;
    
    return true;
};

const validateCNPJ = (cnpj: string): boolean => {
    const cleaned = cnpj.replaceAll(/\D/g, '');
    if (cleaned.length !== 14) return false;
    if (/^(\d)\1+$/.test(cleaned)) return false;
    
    let length = cleaned.length - 2;
    let numbers = cleaned.substring(0, length);
    const digits = cleaned.substring(length);
    let sum = 0;
    let pos = length - 7;
    
    for (let i = length; i >= 1; i--) {
        sum += Number.parseInt(numbers.charAt(length - i)) * pos--;
        if (pos < 2) pos = 9;
    }
    
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== Number.parseInt(digits.charAt(0))) return false;
    
    length = length + 1;
    numbers = cleaned.substring(0, length);
    sum = 0;
    pos = length - 7;
    
    for (let i = length; i >= 1; i--) {
        sum += Number.parseInt(numbers.charAt(length - i)) * pos--;
        if (pos < 2) pos = 9;
    }
    
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== Number.parseInt(digits.charAt(1))) return false;
    
    return true;
};

const validateDocument = (doc: string): boolean => {
    const cleaned = doc.replaceAll(/\D/g, '');
    if (cleaned.length === 11) return validateCPF(doc);
    if (cleaned.length === 14) return validateCNPJ(doc);
    return false;
};

const getCnpjRoot = (cnpj: string): string => cnpj.slice(0, 8);

const isCnpjMatrix = (cnpj: string): boolean => cnpj.slice(8, 12) === '0001';

const getGroupingKey = (doc: string): string => {
    if (doc.length === 14) return getCnpjRoot(doc);
    return doc;
};

interface GroupedItem {
    document: string;
    name: string;
    amount: number;
    dueDate?: string;
    obs?: string;
    count: number;
}

const parseBRNumber = (value: string): number => {
    const cleaned = value.replaceAll(/[^\d,.-]/g, '');
    // Has comma → BR decimal separator
    if (cleaned.includes(',')) {
        return Number.parseFloat(cleaned.replaceAll('.', '').replace(',', '.')) || 0;
    }
    // No comma but has dots: check if dots are thousands separators (groups of 3 digits)
    if (cleaned.includes('.') && /^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
        return Number.parseFloat(cleaned.replaceAll('.', '')) || 0;
    }
    return Number.parseFloat(cleaned) || 0;
};

const groupRowsByDocument = (
    rows: string[][],
    tagColumns: Record<string, number>
): Map<string, GroupedItem> => {
    const grouped = new Map<string, GroupedItem>();

    rows.forEach(row => {
        const doc = (row[tagColumns['PRODUCER_DOCUMENT']] || '').replaceAll(/\D/g, '');
        const name = row[tagColumns['DRAWEE']] || '';
        const amount = parseBRNumber(row[tagColumns['AMOUNT']] || '0');
        const dueDate = tagColumns['DUE_DATE'] !== undefined ? row[tagColumns['DUE_DATE']] : undefined;
        const obs = tagColumns['OBSERVATION'] !== undefined ? row[tagColumns['OBSERVATION']] : undefined;

        if (!doc) return;

        const key = getGroupingKey(doc);
        const existing = grouped.get(key);

        if (existing) {
            existing.amount += amount;
            existing.count += 1;
            if (obs && !existing.obs) existing.obs = obs;
            // For CNPJs: prefer the matrix document (branch 0001)
            if (doc.length === 14 && isCnpjMatrix(doc)) {
                existing.document = doc;
                existing.name = name;
            }
        } else {
            grouped.set(key, { document: doc, name, amount, dueDate, obs, count: 1 });
        }
    });

    return grouped;
};

export default function CreditAnalysisPageForm() {
    const navigate = useNavigate();
    const [batchName, setBatchName] = useState('');
    const [assignor, setAssignor] = useState<AssignorData>({
        id: 'assignor-1',
        name: '',
        data: [],
        columnTags: {},
        validationErrors: new Map()
    });
    const [draggedTag, setDraggedTag] = useState<TagKey | null>(null);
    const [savedTemplates, setSavedTemplates] = useState<Record<string, Record<number, TagKey>>>({});
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [editingCell, setEditingCell] = useState<{ assignorId: string; row: number; col: number } | null>(null);
    const [editValue, setEditValue] = useState('');
    const [isValidated, setIsValidated] = useState(false);

    // Person search state
    const [assignorSearch, setAssignorSearch] = useState('');
    const [assignorResults, setAssignorResults] = useState<LegalPersonResult[]>([]);
    const [isSearchingAssignor, setIsSearchingAssignor] = useState(false);
    const [searchMessage, setSearchMessage] = useState('Buscando...');
    const [selectedPerson, setSelectedPerson] = useState<LegalPersonResult | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const searchDebounce = useRef<NodeJS.Timeout | null>(null);
    const searchAbort = useRef<AbortController | null>(null);

    useEffect(() => {
        getColumnTemplates().then(data => setSavedTemplates(data as any)).catch(() => {});
    }, []);

    // Validação reativa
    useEffect(() => {
        if (assignor.data.length === 0 || Object.keys(assignor.columnTags).length === 0) {
            setAssignor(prev => ({ ...prev, validationErrors: new Map() }));
            return;
        }

        const errors = new Map<number, Set<number>>();
        
        assignor.data.forEach((row, rowIndex) => {
            const errorColumns = new Set<number>();

            Object.entries(assignor.columnTags).forEach(([colIndex, tagKey]) => {
                const col = Number.parseInt(colIndex);
                const value = row[col]?.trim() || '';

                if ((tagKey === 'PRODUCER_DOCUMENT' || tagKey === 'DRAWEE' || tagKey === 'AMOUNT') && !value) {
                    errorColumns.add(col);
                }

                if (tagKey === 'PRODUCER_DOCUMENT' && value && !validateDocument(value)) {
                    errorColumns.add(col);
                }

                if (tagKey === 'AMOUNT' && value) {
                    if (parseBRNumber(value) <= 0) {
                        errorColumns.add(col);
                    }
                }
            });

            if (errorColumns.size > 0) {
                errors.set(rowIndex, errorColumns);
            }
        });

        setAssignor(prev => ({ ...prev, validationErrors: errors }));
    }, [assignor.data, assignor.columnTags]);

    const _updateAssignorName = (name: string) => {
        setAssignor(prev => ({ ...prev, name }));
    };

    const handleAssignorSearch = (value: string) => {
        setAssignorSearch(value);
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        if (searchAbort.current) searchAbort.current.abort();

        if (value.length < MIN_SEARCH_LENGTH) {
            setAssignorResults([]);
            setIsSearchingAssignor(false);
            return;
        }

        setIsSearchingAssignor(true);
        setSearchMessage('Buscando...');
        searchDebounce.current = setTimeout(async () => {
            searchAbort.current = new AbortController();
            const maxRetries = 10;
            for (let attempt = 0; attempt <= maxRetries; attempt++) {
                try {
                    const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());
                    const res = await fetch(`${PERSON_SERVICE_URL}/legal-persons/page-list?legalName=${encodeURIComponent(value)}&page=0&size=10&sort=id,DESC`, {
                        headers: new Headers({
                            'Authorization': `Bearer ${token}`,
                            'x-api-key': API_KEY_PERSON || '',
                            'Content-Type': 'application/json',
                        }),
                        method: 'GET',
                        signal: searchAbort.current.signal,
                    });
                    if (res.ok) {
                        const data = await res.json();
                        setAssignorResults((data.list || []).slice(0, 6));
                        break;
                    } else if (res.status === 504 && attempt < maxRetries) {
                        setSearchMessage('A busca pode demorar um pouco...');
                        continue;
                    } else {
                        const errData = await res.json().catch(() => ({ message: `Erro ${res.status}` }));
                        toast.error(errData.message || `Erro ao buscar cedente (${res.status})`);
                        break;
                    }
                } catch (e: any) {
                    if (e.name === 'AbortError') return;
                    toast.error(`Erro ao buscar cedente: ${e.message || 'Falha na conexão'}`);
                    break;
                }
            }
            setIsSearchingAssignor(false);
            setSearchMessage('Buscando...');
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleSelectAssignor = (person: LegalPersonResult) => {
        setSelectedPerson(person);
        setAssignor(prev => ({ ...prev, id: person.personId, name: person.legalName }));
        setAssignorSearch('');
        setAssignorResults([]);
    };

    const clearAssignor = () => {
        setSelectedPerson(null);
        setAssignor(prev => ({ ...prev, id: 'assignor-1', name: '' }));
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const rows = pastedText.split('\n').filter(row => row.trim());
        const parsedData = rows
            .map(row => row.split('\t'))
            .filter(row => row.some(cell => cell.trim() !== '')); // Remove linhas completamente vazias
        
        setAssignor(prev => ({ ...prev, data: parsedData, columnTags: {} }));
        setIsValidated(false);
        
        toast.info(`${parsedData.length} linhas importadas`);
    };

    const handleDragStart = (tagKey: TagKey) => {
        setDraggedTag(tagKey);
    };

    const handleDrop = (colIndex: number) => {
        if (!draggedTag) return;
        
        const prevColIndex = Object.keys(assignor.columnTags).find(
            key => assignor.columnTags[Number(key)] === draggedTag
        );
        
        const newTags = { ...assignor.columnTags };
        if (prevColIndex) delete newTags[Number(prevColIndex)];
        newTags[colIndex] = draggedTag;
        
        setAssignor(prev => ({ ...prev, columnTags: newTags }));
        
        setDraggedTag(null);
    };

    const removeTag = (colIndex: number) => {
        const newTags = { ...assignor.columnTags };
        delete newTags[colIndex];
        setAssignor(prev => ({ ...prev, columnTags: newTags }));
    };

    const isTagUsed = (tagKey: TagKey) => {
        return Object.values(assignor.columnTags).includes(tagKey);
    };

    const removeRow = (rowIndex: number) => {
        // Atualizar índices de erro
        const newErrors = new Map<number, Set<number>>();
        assignor.validationErrors.forEach((errorCols, rowIdx) => {
            if (rowIdx < rowIndex) {
                newErrors.set(rowIdx, errorCols);
            } else if (rowIdx > rowIndex) {
                newErrors.set(rowIdx - 1, errorCols);
            }
            // rowIdx === rowIndex é removido (não adiciona)
        });

        setAssignor(prev => ({
            ...prev,
            data: prev.data.filter((_, i) => i !== rowIndex),
            validationErrors: newErrors
        }));
    };

    const removeColumn = (colIndex: number) => {
        const newData = assignor.data.map(row => row.filter((_, j) => j !== colIndex));
        const newTags: Record<number, TagKey> = {};
        
        Object.keys(assignor.columnTags).forEach(key => {
            const idx = Number.parseInt(key);
            if (idx < colIndex) newTags[idx] = assignor.columnTags[idx];
            if (idx > colIndex) newTags[idx - 1] = assignor.columnTags[idx];
        });

        // Atualizar índices de erro
        const newErrors = new Map<number, Set<number>>();
        assignor.validationErrors.forEach((errorCols, rowIdx) => {
            const newErrorCols = new Set<number>();
            errorCols.forEach(col => {
                if (col < colIndex) newErrorCols.add(col);
                if (col > colIndex) newErrorCols.add(col - 1);
                // col === colIndex é removido (não adiciona)
            });
            if (newErrorCols.size > 0) {
                newErrors.set(rowIdx, newErrorCols);
            }
        });
        
        setAssignor(prev => ({ ...prev, data: newData, columnTags: newTags, validationErrors: newErrors }));
    };

    const saveTemplate = () => {
        if (!templateName.trim()) {
            toast.error('Digite um nome para o template');
            return;
        }

        if (Object.keys(assignor.columnTags).length === 0) {
            toast.error('Configure as tags antes de salvar');
            return;
        }

        const newTemplates = { ...savedTemplates, [templateName]: assignor.columnTags };
        setSavedTemplates(newTemplates);
        saveColumnTemplate(templateName, assignor.columnTags).catch(() => {});
        toast.success(`Template "${templateName}" salvo com sucesso`);
        setTemplateName('');
        setShowSaveDialog(false);
    };

    const loadTemplate = (name: string) => {
        if (savedTemplates[name]) {
            setAssignor(prev => ({ ...prev, columnTags: savedTemplates[name] }));
            toast.info(`Template "${name}" carregado`);
        }
    };

    const _deleteTemplate = (name: string) => {
        const newTemplates = { ...savedTemplates };
        delete newTemplates[name];
        setSavedTemplates(newTemplates);
        deleteColumnTemplate(name).catch(() => {});
        toast.info(`Template "${name}" removido`);
    };

    const clearData = () => {
        setAssignor(prev => ({ ...prev, data: [] }));
        toast.info('Dados limpos');
    };

    const clearTags = () => {
        setAssignor(prev => ({ ...prev, columnTags: {} }));
        toast.info('Tags removidas');
    };

    const startEditing = (rowIndex: number, colIndex: number, currentValue: string) => {
        setEditingCell({ assignorId: assignor.id, row: rowIndex, col: colIndex });
        setEditValue(currentValue);
    };

    const saveEdit = () => {
        if (editingCell) {
            const newData = assignor.data.map((row, i) =>
                i === editingCell.row
                    ? row.map((cell, j) => j === editingCell.col ? editValue : cell)
                    : row
            );
            
            setAssignor(prev => ({ ...prev, data: newData }));
            
            setEditingCell(null);
            setEditValue('');
        }
    };

    const cancelEdit = () => {
        setEditingCell(null);
        setEditValue('');
    };

    const handleValidate = () => {
        const requiredTags: TagKey[] = ['PRODUCER_DOCUMENT', 'DRAWEE', 'AMOUNT'];
        const assignedTags = Object.values(assignor.columnTags);
        const missingTags = requiredTags.filter(tag => !assignedTags.includes(tag));
        if (missingTags.length > 0) {
            toast.error(`Tags obrigatórias faltando: ${missingTags.map(t => TAGS[t].name).join(', ')}`);
            return;
        }
        if (assignor.validationErrors.size > 0) {
            toast.error(`Existem ${assignor.validationErrors.size} linha(s) com erro. Corrija antes de validar.`);
            return;
        }

        // Map column tags to indices
        const tagColumns: Record<string, number> = {};
        Object.entries(assignor.columnTags).forEach(([col, tag]) => {
            tagColumns[tag] = Number.parseInt(col);
        });

        // Group by document (CPF: exact match, CNPJ: root 8 digits keeping matrix)
        const grouped = groupRowsByDocument(assignor.data, tagColumns);

        // Rebuild data with consolidated rows
        const docColIdx = tagColumns['PRODUCER_DOCUMENT'];
        const nameColIdx = tagColumns['DRAWEE'];
        const amountColIdx = tagColumns['AMOUNT'];
        const consolidatedData: string[][] = [];
        let mergedCount = 0;

        Array.from(grouped.values()).forEach((info) => {
            const row = new Array(assignor.data[0]?.length || 3).fill('');
            const doc = info.document;
            row[docColIdx] = doc.length === 11
                ? `${doc.slice(0,3)}.${doc.slice(3,6)}.${doc.slice(6,9)}-${doc.slice(9)}`
                : doc.length === 14
                ? `${doc.slice(0,2)}.${doc.slice(2,5)}.${doc.slice(5,8)}/${doc.slice(8,12)}-${doc.slice(12)}`
                : doc;
            row[nameColIdx] = info.name;
            row[amountColIdx] = info.amount.toFixed(2);
            if (tagColumns['DUE_DATE'] !== undefined && info.dueDate) row[tagColumns['DUE_DATE']] = info.dueDate;
            if (tagColumns['OBSERVATION'] !== undefined && info.obs) row[tagColumns['OBSERVATION']] = info.obs;
            consolidatedData.push(row);
            if (info.count > 1) mergedCount++;
        });

        setAssignor(prev => ({ ...prev, data: consolidatedData, validationErrors: new Map() }));
        setIsValidated(true);

        if (mergedCount > 0) {
            toast.success(`Validado: ${consolidatedData.length} sacados (${mergedCount} agrupados por CPF (exato) / CNPJ (raiz))`);
        } else {
            toast.success(`Validado: ${consolidatedData.length} sacados`);
        }
    };

    const handleSubmit = async () => {
        if (!batchName.trim()) {
            toast.error('Digite o nome do lote');
            return;
        }

        const requiredTags: TagKey[] = ['PRODUCER_DOCUMENT', 'DRAWEE', 'AMOUNT'];
        
        if (!assignor.name.trim()) {
            toast.error('Selecione um cedente');
            return;
        }

        if (assignor.data.length === 0) {
            toast.error('Importe os dados antes de processar');
            return;
        }

        const assignedTags = Object.values(assignor.columnTags);
        const missingTags = requiredTags.filter(tag => !assignedTags.includes(tag));

        if (missingTags.length > 0) {
            toast.error(`Tags obrigatórias faltando: ${missingTags.map(t => TAGS[t].name).join(', ')}`);
            return;
        }

        if (assignor.validationErrors.size > 0) {
            toast.error(`Existem ${assignor.validationErrors.size} linha(s) com erro`);
            return;
        }

        // Map column tags to indices
        const tagColumns: Record<string, number> = {};
        Object.entries(assignor.columnTags).forEach(([col, tag]) => {
            tagColumns[tag] = Number.parseInt(col);
        });

        // Build API payload (deduplicate: CPF exact match, CNPJ by root keeping matrix)
        const grouped = groupRowsByDocument(assignor.data, tagColumns);
        const items = Array.from(grouped.values()).map(info => ({
            document: info.document,
            draweeName: info.name,
            requestedAmount: info.amount,
            dueDate: info.dueDate || undefined,
            observation: info.obs || undefined,
        }));

        const request: CreateBatchRequest = {
            batchName,
            assignor: {
                uuid: assignor.id,
                name: assignor.name,
                document: selectedPerson?.govTaxId || '',
            },
            items,
        };

        try {
            setIsSubmitting(true);
            toast.info('Enviando lote para processamento...');
            const result = await createBatchProcessing(request);
            toast.success(`Lote "${result.batchNumber}" criado com ${result.totalItems} análises — status: ${result.status}`);
            navigate('/credit-analysis/processamentos');
        } catch (error: any) {
            toast.error(`Erro ao criar lote: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="px-4 pt-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 min-h-screen">
            <PageHeader 
                title="Novo Processamento de Análise de Crédito" 
                apiVersion="Credit Analysis API - 1.0.0-mock" 
                breadcrumb={[
                    { label: 'Análise de Crédito', url: '/credit-analysis/processamentos' },
                    { label: 'Novo Processamento', url: '#' }
                ]}
            >
                <div className="flex gap-2 pt-6">
                    <button
                        onClick={() => navigate('/credit-analysis/processamentos')}
                        className="bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-900 dark:text-gray-100 font-medium rounded px-5 py-2.5"
                    >
                        Cancelar
                    </button>
                    {assignor.data.length > 0 && !isValidated && (
                        <button
                            onClick={handleValidate}
                            className="bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-medium rounded px-5 py-2.5 flex items-center gap-2"
                        >
                            Validar Dados
                        </button>
                    )}
                    {isValidated && (
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="bg-blue-500 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium rounded px-5 py-2.5 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? <><FaSpinner className="animate-spin" /> Processando...</> : <><FaSave /> Processar Lote</>}
                        </button>
                    )}
                </div>
            </PageHeader>

            <div className="mt-6 space-y-6">
                {/* Nome do Lote */}
                <div className="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg border border-gray-200 dark:border-slate-600">
                    <label className="block text-sm font-semibold mb-2 dark:text-gray-100">
                        Nome do Lote *
                    </label>
                    <input
                        type="text"
                        value={batchName}
                        onChange={(e) => setBatchName(e.target.value)}
                        placeholder="Ex: Processamento Grande - 100 Sacados - 15/01/2026"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-800 dark:text-gray-100"
                    />
                </div>

                {/* Cedente */}
                <div className="bg-gray-50 dark:bg-slate-700 p-6 rounded-lg border-2 border-gray-300 dark:border-slate-600">
                    {/* Header do Cedente */}
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-lg font-semibold dark:text-gray-100">Cedente</span>
                        {selectedPerson ? (
                            <div className="flex-1 flex items-center gap-3 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded">
                                <span className="text-sm font-medium dark:text-gray-100">
                                    {selectedPerson.govTaxIdFormatted ? `${selectedPerson.govTaxIdFormatted} - ${selectedPerson.legalName}` : selectedPerson.legalName}
                                </span>
                                <button
                                    onClick={clearAssignor}
                                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                    title="Remover cedente"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        ) : (
                            <div className="flex-1 relative">
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={assignorSearch}
                                        onChange={(e) => handleAssignorSearch(e.target.value)}
                                        placeholder="Buscar cedente por nome ou CPF/CNPJ (mín. 3 caracteres)"
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-800 dark:text-gray-100"
                                    />
                                </div>
                                {(isSearchingAssignor || assignorResults.length > 0) && (
                                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded shadow-lg max-h-64 overflow-y-auto">
                                        {isSearchingAssignor ? (
                                            <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                {searchMessage}
                                            </div>
                                        ) : (
                                            assignorResults.map((person) => (
                                                    <button
                                                        key={person.personId}
                                                        onClick={() => handleSelectAssignor(person)}
                                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 text-sm dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                                                    >
                                                        <span className="font-medium">{person.govTaxIdFormatted}</span>
                                                        {person.govTaxIdFormatted && ' - '}
                                                        {person.legalName}
                                                    </button>
                                                ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                        {/* Tags e Controles */}
                        <div className="bg-white dark:bg-slate-800 p-4 rounded border border-gray-200 dark:border-slate-600 mb-4">
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold dark:text-gray-100">Tags Disponíveis</h4>
                                    <div className="flex gap-2">
                                        {assignor.data.length > 0 && (
                                            <button
                                                onClick={clearData}
                                                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-sm flex items-center gap-2"
                                            >
                                                <FaEraser /> Limpar Dados
                                            </button>
                                        )}
                                        {Object.keys(assignor.columnTags).length > 0 && (
                                            <button
                                                onClick={clearTags}
                                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm flex items-center gap-2"
                                            >
                                                <FaTimes /> Remover Tags
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {(Object.keys(savedTemplates).length > 0 || Object.keys(assignor.columnTags).length > 0) && (
                                    <div className="flex gap-2 justify-end">
                                        {Object.keys(savedTemplates).length > 0 && (
                                            <select
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        loadTemplate(e.target.value);
                                                        e.target.value = '';
                                                    }
                                                }}
                                                className="appearance-none bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded px-3 py-1 pr-8 text-sm bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M2%204l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_0.5rem_center]"
                                            >
                                                <option value="">Carregar template...</option>
                                                {Object.keys(savedTemplates).map(name => (
                                                    <option key={name} value={name}>{name}</option>
                                                ))}
                                            </select>
                                        )}
                                        {Object.keys(assignor.columnTags).length > 0 && (
                                            <button
                                                onClick={() => setShowSaveDialog(true)}
                                                className="bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white px-3 py-1 rounded text-sm flex items-center gap-2"
                                            >
                                                <FaSave /> Salvar Template
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {(Object.keys(TAGS) as TagKey[]).map(key => (
                                    <div
                                        key={key}
                                        draggable
                                        onDragStart={() => handleDragStart(key)}
                                        className={`${TAGS[key].color} ${
                                            isTagUsed(key) ? 'opacity-40 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
                                        } text-white px-4 py-2 rounded font-medium transition-all hover:shadow-lg`}
                                    >
                                        {TAGS[key].name}
                                        {key === 'PRODUCER_DOCUMENT' || key === 'DRAWEE' || key === 'AMOUNT' ? ' *' : ''}
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">* Campos obrigatórios</p>
                        </div>

                        {/* Área de colagem */}
                        {assignor.data.length === 0 && (
                            <div
                                contentEditable
                                onPaste={handlePaste}
                                suppressContentEditableWarning
                                className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center bg-white dark:bg-slate-800 cursor-text focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 mb-4"
                            >
                                <FaUpload className="mx-auto text-3xl text-gray-400 dark:text-gray-500 mb-2" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    Clique aqui e cole os dados do Excel (Ctrl+V ou Cmd+V)
                                </p>
                            </div>
                        )}

                        {/* Tabela de dados */}
                        {assignor.data.length > 0 && (
                            <div className="overflow-x-auto border border-gray-200 dark:border-slate-600 rounded-lg">
                                <table className="w-full bg-white dark:bg-slate-800">
                                    <thead>
                                        <tr>
                                            <th className="bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 p-2 w-12"></th>
                                            {assignor.data[0].map((_, colIndex) => (
                                                <th
                                                    key={colIndex}
                                                    onDragOver={(e) => e.preventDefault()}
                                                    onDrop={() => handleDrop(colIndex)}
                                                    className="bg-blue-50 dark:bg-slate-700 border-2 border-dashed border-blue-300 dark:border-blue-600 p-3 min-w-[150px]"
                                                >
                                                    {assignor.columnTags[colIndex] ? (
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className={`${TAGS[assignor.columnTags[colIndex]].color} text-white px-2 py-1 rounded text-sm font-medium`}>
                                                                {TAGS[assignor.columnTags[colIndex]].name}
                                                            </span>
                                                            <div className="flex gap-1">
                                                                <button
                                                                    onClick={() => removeTag(colIndex)}
                                                                    className="bg-orange-500 hover:bg-orange-600 text-white rounded px-2 py-1 text-xs"
                                                                    title="Remover tag"
                                                                >
                                                                    ↺
                                                                </button>
                                                                <button
                                                                    onClick={() => removeColumn(colIndex)}
                                                                    className="bg-red-500 hover:bg-red-600 text-white rounded px-2 py-1 text-xs"
                                                                    title="Remover coluna"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className="text-gray-500 dark:text-gray-400 text-sm">Arraste tag aqui</span>
                                                            <button
                                                                onClick={() => removeColumn(colIndex)}
                                                                className="bg-red-500 hover:bg-red-600 text-white rounded px-2 py-1 text-xs"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignor.data
                                            .map((row, rowIndex) => ({ row, rowIndex }))
                                            .sort((a, b) => {
                                                const aHasError = assignor.validationErrors.has(a.rowIndex);
                                                const bHasError = assignor.validationErrors.has(b.rowIndex);
                                                if (aHasError && !bHasError) return -1;
                                                if (!aHasError && bHasError) return 1;
                                                return 0;
                                            })
                                            .map(({ row, rowIndex }) => {
                                            const rowErrors = assignor.validationErrors.get(rowIndex);
                                            const hasError = rowErrors && rowErrors.size > 0;
                                            
                                            return (
                                                <tr 
                                                    key={rowIndex} 
                                                    className={`${hasError ? 'bg-red-100 dark:bg-red-900/30' : rowIndex % 2 === 0 ? 'bg-gray-50 dark:bg-slate-700' : 'bg-white dark:bg-slate-800'}`}
                                                >
                                                    <td className="border border-gray-300 dark:border-slate-600 p-2 text-center">
                                                        <button
                                                            onClick={() => removeRow(rowIndex)}
                                                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                            title="Remover linha"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                    {row.map((cell, colIndex) => {
                                                        const hasFieldError = rowErrors?.has(colIndex);
                                                        const isEditing = editingCell?.assignorId === assignor.id && editingCell?.row === rowIndex && editingCell?.col === colIndex;
                                                        
                                                        return (
                                                            <td 
                                                                key={colIndex} 
                                                                className={`border border-gray-300 dark:border-slate-600 p-2 ${
                                                                    hasFieldError 
                                                                        ? 'bg-red-600 dark:bg-red-700 text-white font-semibold' 
                                                                        : 'dark:text-gray-200'
                                                                } ${isEditing ? '' : 'cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-600'}`}
                                                                title={hasFieldError ? 'Campo obrigatório vazio ou inválido' : 'Clique para editar'}
                                                                onClick={() => !isEditing && startEditing(rowIndex, colIndex, cell)}
                                                            >
                                                                {isEditing ? (
                                                                    <input
                                                                        type="text"
                                                                        value={editValue}
                                                                        onChange={(e) => setEditValue(e.target.value)}
                                                                        onBlur={saveEdit}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') saveEdit();
                                                                            if (e.key === 'Escape') cancelEdit();
                                                                        }}
                                                                        autoFocus
                                                                        className="w-full px-1 py-0 border-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                                                    />
                                                                ) : (
                                                                    cell
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                {/* Dialog de salvar template */}
                {showSaveDialog && assignor.data.length > 0 && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        onClick={() => setShowSaveDialog(false)}
                    >
                        <div
                            className="bg-white dark:bg-slate-800 rounded-lg p-6 min-w-[400px] shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-xl font-semibold mb-4 dark:text-gray-100">Salvar Template</h3>
                            <input
                                type="text"
                                placeholder="Nome do template"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && saveTemplate()}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-700 dark:text-gray-100 mb-4"
                                autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => setShowSaveDialog(false)}
                                    className="bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-900 dark:text-gray-100 px-4 py-2 rounded font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={saveTemplate}
                                    className="bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white px-4 py-2 rounded font-medium flex items-center gap-2"
                                >
                                    <FaSave /> Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
